import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'
import { showImagePreview } from 'vant'
import { buildUrl } from './http'
import { isNativeApp } from './native'
import { getStorage } from './storage'

export interface PickedFile {
  file: File
  previewUrl: string
}

/** 用户主动取消选图时抛出，调用方应静默处理，不要 toast「失败」。 */
export class ImagePickCancelledError extends Error {
  constructor(message = '已取消选择图片') {
    super(message)
    this.name = 'ImagePickCancelledError'
  }
}

export function isImagePickCancelled(error: unknown): boolean {
  return error instanceof ImagePickCancelledError
    || (error instanceof Error && (
      error.name === 'ImagePickCancelledError'
      || /cancel|User cancelled|No image picked|picker was cancelled/i.test(error.message)
    ))
}

/**
 * 选择图片。
 * - Native：Capacitor Camera Prompt（相册/拍照）
 * - Web/H5：直接走 input[type=file]，避免 CameraSource.Prompt 依赖未注册的 pwa-action-sheet 导致永久挂起
 */
export async function chooseImage(): Promise<PickedFile | null> {
  if (!isNativeApp()) {
    return chooseImageViaFileInput()
  }

  try {
    const image = await Camera.getPhoto({
      quality: 85,
      allowEditing: false,
      resultType: CameraResultType.Uri,
      source: CameraSource.Prompt,
      // Web 兜底：即便误走 web 路径也使用 file input，不依赖 pwa-elements
      webUseInput: true,
    })

    if (!image?.webPath) {
      throw new ImagePickCancelledError()
    }

    const blob = await fetch(image.webPath).then(response => response.blob())
    const extension = image.format || 'jpg'
    const mime = blob.type || `image/${extension === 'jpg' ? 'jpeg' : extension}`
    const file = new File([blob], `image-${Date.now()}.${extension}`, { type: mime })
    return { file, previewUrl: image.webPath }
  }
  catch (error) {
    if (isImagePickCancelled(error)) {
      throw new ImagePickCancelledError()
    }
    // 原生 Camera 失败时降级到 file input（部分 WebView 或权限场景）
    try {
      return await chooseImageViaFileInput()
    }
    catch (fallbackError) {
      if (isImagePickCancelled(fallbackError)) {
        throw new ImagePickCancelledError()
      }
      throw error instanceof Error ? error : new Error('选择图片失败')
    }
  }
}

function chooseImageViaFileInput(): Promise<PickedFile | null> {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('当前环境不支持选择图片'))
      return
    }

    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.style.position = 'fixed'
    input.style.left = '-9999px'
    input.style.opacity = '0'
    document.body.appendChild(input)

    let settled = false
    const cleanup = () => {
      if (input.parentNode)
        input.parentNode.removeChild(input)
      window.removeEventListener('focus', onWindowFocus)
    }
    const settle = (fn: () => void) => {
      if (settled)
        return
      settled = true
      cleanup()
      fn()
    }

    // 用户取消 file picker 时部分浏览器不触发 change，用 focus 兜底
    const onWindowFocus = () => {
      window.setTimeout(() => {
        if (!settled && !input.files?.length) {
          settle(() => reject(new ImagePickCancelledError()))
        }
      }, 400)
    }

    input.addEventListener('change', () => {
      const file = input.files?.[0]
      if (!file) {
        settle(() => reject(new ImagePickCancelledError()))
        return
      }
      const previewUrl = URL.createObjectURL(file)
      settle(() => resolve({ file, previewUrl }))
    }, { once: true })

    window.addEventListener('focus', onWindowFocus, { once: true })
    input.click()
  })
}

const DEFAULT_UPLOAD_TIMEOUT_MS = 30000

export async function uploadFile<T>(
  path: string,
  file: File,
  fieldName = 'file',
  extra: Record<string, string> = {},
  timeoutMs = DEFAULT_UPLOAD_TIMEOUT_MS,
): Promise<T> {
  const form = new FormData()
  form.append(fieldName, file)
  Object.entries(extra).forEach(([key, value]) => form.append(key, value))
  const token = getStorage('token')

  const controller = timeoutMs ? new AbortController() : null
  const timeoutId = controller ? setTimeout(() => controller.abort(), timeoutMs) : undefined

  let response: Response
  try {
    response = await fetch(buildUrl(path), {
      method: 'POST',
      body: form,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      signal: controller?.signal,
    })
  }
  catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error('上传超时，请检查网络后重试')
    }
    throw error
  }
  finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId)
    }
  }

  const payload = await response.json()
  if (!response.ok || (typeof payload.code === 'number' && payload.code !== 0 && payload.code !== 200)) {
    throw new Error(payload.msg || payload.message || '上传失败')
  }
  return payload.data as T
}

export function uploadFileWithProgress<T>(
  path: string,
  file: File,
  fieldName = 'file',
  extra: Record<string, string> = {},
  onProgress?: (percentage: number) => void,
  timeoutMs = DEFAULT_UPLOAD_TIMEOUT_MS,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const form = new FormData()
    form.append(fieldName, file)
    Object.entries(extra).forEach(([key, value]) => form.append(key, value))

    const request = new XMLHttpRequest()
    request.open('POST', buildUrl(path))
    request.timeout = timeoutMs
    const token = getStorage('token')
    if (token)
      request.setRequestHeader('Authorization', `Bearer ${token}`)

    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable)
        onProgress?.(Math.round(event.loaded / event.total * 100))
    })
    request.addEventListener('load', () => {
      try {
        const payload = JSON.parse(request.responseText)
        if (request.status < 200 || request.status >= 300
          || (typeof payload.code === 'number' && payload.code !== 0 && payload.code !== 200)) {
          reject(new Error(payload.msg || payload.message || '上传失败'))
          return
        }
        onProgress?.(100)
        resolve(payload.data as T)
      }
      catch {
        reject(new Error('上传响应格式错误'))
      }
    })
    request.addEventListener('error', () => reject(new Error('上传失败')))
    request.addEventListener('timeout', () => reject(new Error('上传超时，请检查网络后重试')))
    request.send(form)
  })
}

export function previewImages(urls: string[], startPosition = 0) {
  showImagePreview({ images: urls, startPosition })
}
